// Ported from WOOF1_eCRF_v7.html: computeIRECISTResponse (line 6108) is the exact
// decision engine; the SOM/nadir/delta pipeline below is ported from
// renderIRECISTSection (lines 6270-6392). Modeled on Seymour et al. 2017.

export interface IrecistResponseCtx {
  somCurrent: number | null;
  somBaseline: number | null;
  somNadir: number | null;
  deltaBaseline: number | null;
  deltaNadir: number | null;
  hasNewLesions: boolean;
  nontargetStatus: string;
  prevResponse: string;
}

export interface IrecistResponseResult {
  code: "iCR" | "iPR" | "iSD" | "iUPD" | "iCPD" | "NE";
  label: string;
  reasons: string[];
}

export function computeIRECISTResponse(ctx: IrecistResponseCtx): IrecistResponseResult {
  const reasons: string[] = [];
  if (ctx.somCurrent === null || ctx.somBaseline === null) {
    return { code: "NE", label: "Not Evaluable", reasons: ["SOM courant ou baseline manquant"] };
  }

  const dB = ctx.deltaBaseline;
  const dN = ctx.deltaNadir;
  const nontarget = ctx.nontargetStatus || "Non-CR/Non-PD";

  // Progression sur cibles : >= +20% relatif ET >= +5mm absolu vs nadir.
  const targetsPD = dN !== null && dN >= 20 && ctx.somCurrent - (ctx.somNadir as number) >= 5;
  const nontargetPD = nontarget === "PD";

  if (targetsPD || nontargetPD || ctx.hasNewLesions) {
    if (targetsPD) reasons.push(`Δ vs nadir = ${dN!.toFixed(1)}% (≥ +20%) et augmentation absolue ≥ 5 mm`);
    if (nontargetPD) reasons.push("Progression non-équivoque des lésions non-cibles");
    if (ctx.hasNewLesions) reasons.push("Apparition de nouvelle(s) lésion(s)");

    if (ctx.prevResponse === "iUPD") {
      reasons.push("Visite précédente = iUPD → confirmation possible");
      return { code: "iCPD", label: "Confirmed Progressive Disease", reasons };
    }
    return { code: "iUPD", label: "Unconfirmed Progressive Disease", reasons };
  }

  if (ctx.somCurrent === 0 && nontarget === "CR") {
    reasons.push("SOM cible = 0 mm (toutes lésions cibles disparues)");
    reasons.push("Lésions non-cibles : CR");
    reasons.push("Aucune nouvelle lésion");
    return { code: "iCR", label: "Complete Response", reasons };
  }

  if (dB !== null && dB <= -30) {
    reasons.push(`Δ vs baseline = ${dB.toFixed(1)}% (≤ −30%)`);
    reasons.push("Pas de progression ni nouvelle lésion");
    return { code: "iPR", label: "Partial Response", reasons };
  }

  reasons.push(`Δ vs baseline = ${dB !== null ? dB.toFixed(1) + "%" : "—"} (entre −30% et +20%)`);
  reasons.push("Pas de progression non-cibles ni nouvelle lésion");
  return { code: "iSD", label: "Stable Disease", reasons };
}

export interface IrecistLesion {
  diameter?: number | string | null;
}

export interface IrecistVisitData {
  target_lesions?: IrecistLesion[];
  nontarget_lesions?: unknown[];
  new_lesions?: unknown[];
  som_target?: number | string | null;
  nontarget_status?: string | null;
  response?: string | null;
  suggested_response?: string | null;
}

export interface IrecistVisitEntry {
  vk: string;
  irecist?: IrecistVisitData;
}

function sumTargetDiameters(lesions: IrecistLesion[] | undefined): number | null {
  if (!Array.isArray(lesions) || lesions.length === 0) return null;
  let sum = 0;
  let n = 0;
  for (const l of lesions) {
    const d = typeof l.diameter === "number" ? l.diameter : parseFloat(String(l.diameter ?? ""));
    if (!Number.isNaN(d) && d >= 0) {
      sum += d;
      n++;
    }
  }
  return n > 0 ? sum : null;
}

function somForVisit(vk: string, ir: IrecistVisitData): number | null {
  if (vk === "V1") return sumTargetDiameters(ir.target_lesions);
  const m = typeof ir.som_target === "number" ? ir.som_target : parseFloat(String(ir.som_target ?? ""));
  return !Number.isNaN(m) && m > 0 ? m : null;
}

// Ported from getPrevIRECISTResponse (source line 6162): walks V1->V4->V6->V9->extras,
// keeping the most recent response/suggested_response strictly before currentVk.
export function getPrevIRECISTResponse(visits: IrecistVisitEntry[], visitOrder: string[], currentVk: string): string {
  let prev = "";
  for (const vk of visitOrder) {
    if (vk === currentVk) break;
    const ir = visits.find((v) => v.vk === vk)?.irecist ?? {};
    if (ir.response) prev = ir.response;
    else if (ir.suggested_response) prev = ir.suggested_response;
  }
  return prev;
}

export interface IrecistSummary {
  somCurrent: number | null;
  somBaseline: number | null;
  somNadir: number | null;
  deltaBaseline: number | null;
  deltaNadir: number | null;
  suggestion: IrecistResponseResult | null;
}

// Full pipeline ported from renderIRECISTSection (source lines 6270-6367).
// `visits` must include the current visit's in-progress data (merged in by the caller)
// so the "current visit" branch of the nadir loop sees live edits.
export function computeIrecistSummary(
  visits: IrecistVisitEntry[],
  visitOrder: string[],
  currentVk: string
): IrecistSummary {
  const isBaseline = currentVk === "V1";
  const v1ir = visits.find((v) => v.vk === "V1")?.irecist ?? {};
  const current = visits.find((v) => v.vk === currentVk)?.irecist ?? {};

  let somCurrent: number | null;
  if (isBaseline) {
    somCurrent = sumTargetDiameters(current.target_lesions);
  } else {
    const manual = typeof current.som_target === "number" ? current.som_target : parseFloat(String(current.som_target ?? ""));
    somCurrent = !Number.isNaN(manual) && manual > 0 ? manual : null;
  }

  let somBaseline: number | null = sumTargetDiameters(v1ir.target_lesions);
  if (somBaseline === null && v1ir.som_target) {
    const b = typeof v1ir.som_target === "number" ? v1ir.som_target : parseFloat(String(v1ir.som_target));
    somBaseline = !Number.isNaN(b) ? b : null;
  }
  if (isBaseline) somBaseline = somCurrent;

  let somNadir: number | null = null;
  for (const vk of visitOrder) {
    const ir = visits.find((v) => v.vk === vk)?.irecist ?? {};
    let somX = somForVisit(vk, ir);
    if (vk === currentVk && somCurrent !== null) somX = somCurrent;
    if (somX !== null && (somNadir === null || somX < somNadir)) somNadir = somX;
    if (vk === currentVk) break;
  }

  const deltaBaseline =
    somCurrent !== null && somBaseline !== null && somBaseline > 0 ? ((somCurrent - somBaseline) / somBaseline) * 100 : null;
  const deltaNadir =
    somCurrent !== null && somNadir !== null && somNadir > 0 ? ((somCurrent - somNadir) / somNadir) * 100 : null;

  let suggestion: IrecistResponseResult | null = null;
  if (!isBaseline) {
    const newLesions = Array.isArray(current.new_lesions) ? current.new_lesions : [];
    suggestion = computeIRECISTResponse({
      somCurrent,
      somBaseline,
      somNadir,
      deltaBaseline,
      deltaNadir,
      hasNewLesions: newLesions.length > 0,
      nontargetStatus: current.nontarget_status || "",
      prevResponse: getPrevIRECISTResponse(visits, visitOrder, currentVk),
    });
  }

  return { somCurrent, somBaseline, somNadir, deltaBaseline, deltaNadir, suggestion };
}
